import React, { useState, useMemo } from "react";
import { Heading } from "../ui/Heading";
import { Input } from "../ui/Input";
import { getThumbnailUrl } from "../../utils/imageUtils";

export interface ShapeLibraryProps {
  onSelectShape?: (shapeUrl: string, shapeName?: string) => void;
  availableShapes?: Array<{ id: string; name: string; url: string; category?: string }>;
}

export const ShapeLibrary: React.FC<ShapeLibraryProps> = ({ onSelectShape, availableShapes = [] }) => {
  const [searchQuery, setSearchQuery] = useState("");

  // Only use shapes from the assets library (selected shapes for the product)
  const allShapes = useMemo(() => {
    return availableShapes || [];
  }, [availableShapes]);

  // Group shapes by category for display
  const shapesByCategory = useMemo(() => {
    const grouped: Record<string, typeof allShapes> = {};
    allShapes.forEach((shape) => {
      const category = shape.category || "Other";
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(shape);
    });
    return grouped;
  }, [allShapes]);

  // Filter shapes based on search query
  const filteredShapesByCategory = useMemo(() => {
    if (!searchQuery.trim()) {
      return shapesByCategory;
    }
    const query = searchQuery.toLowerCase();
    const filtered: Record<string, typeof allShapes> = {};
    Object.entries(shapesByCategory).forEach(([category, shapes]) => {
      const matchingShapes = shapes.filter(
        (shape) =>
          shape.name.toLowerCase().includes(query) ||
          category.toLowerCase().includes(query)
      );
      if (matchingShapes.length > 0) {
        filtered[category] = matchingShapes;
      }
    });
    return filtered;
  }, [searchQuery, shapesByCategory]);

  const handleSelectShape = (shape: typeof allShapes[0]) => {
    if (onSelectShape) {
      onSelectShape(shape.url, shape.name);
    }
  };

  return (
    <div>
      <Heading size="sm" className="mb-4">Shape Library</Heading>
      
      {/* Search Input */}
      <div className="mb-4">
        <Input
          type="text"
          placeholder="Search shapes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Scrollable List by Category */}
      <div className="max-h-96 overflow-y-auto space-y-4">
        {Object.entries(filteredShapesByCategory).map(([category, shapes]) => (
          <div key={category}>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">
              {category}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {shapes.map((shape) => (
                <button
                  key={shape.id}
                  type="button"
                  onClick={() => handleSelectShape(shape)}
                  className="
                    aspect-square border border-gray-300 rounded overflow-hidden
                    hover:border-blue-400 hover:bg-blue-50 transition-colors
                    flex flex-col items-center justify-center p-2 bg-white
                  "
                  title={shape.name}
                >
                  <div className="flex-1 w-full flex items-center justify-center">
                    <img
                      src={getThumbnailUrl(shape.url)}
                      alt={shape.name}
                      className="w-full h-full object-contain max-w-full max-h-full"
                      style={{ 
                        maxWidth: "100%", 
                        maxHeight: "100%",
                        userSelect: "none",
                        pointerEvents: "none",
                        WebkitUserDrag: "none",
                      } as React.CSSProperties}
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        const fallback = target.nextElementSibling as HTMLElement;
                        if (fallback) {
                          fallback.style.display = "flex";
                        }
                      }}
                    />
                    <div className="text-2xl hidden" style={{ display: "none" }}>
                      🎨
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 text-center truncate w-full mt-1">
                    {shape.name}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
        {Object.keys(filteredShapesByCategory).length === 0 && (
          <div className="text-sm text-gray-500 text-center py-8">
            {searchQuery.trim() ? (
              <>No shapes found matching "{searchQuery}"</>
            ) : (
              <>No shapes available. Please select shapes from the assets library in the product settings.</>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShapeLibrary;

